/**
 * Standard-Custom field-definition draft helpers (Origin StandardCustomFieldsEditor).
 *
 * Keys are slugified live on label edit (Origin L6789–6791), then carried through
 * compile from `field.key` — not re-slugified at submit.
 */
import type { PimCustomField } from '@/types/origin'
import { slugifyFieldKey } from '@/lib/sales-workflow/pimCustomFieldKey'

export interface StandardCustomFieldDraft {
  key: string
  label: string
  allowsFile: boolean
}

export function createEmptyStandardCustomField(index: number): StandardCustomFieldDraft {
  return {
    key: slugifyFieldKey('', index),
    label: '',
    allowsFile: false,
  }
}

export function withSlugifiedLabel(
  field: StandardCustomFieldDraft,
  label: string,
  index: number
): StandardCustomFieldDraft {
  return {
    ...field,
    label,
    key: slugifyFieldKey(label, index),
  }
}

/** Wire shape for OriginSpec / changes.std_custom — Origin L5012 / L7316. */
export function toCustomFieldsDefinition(
  fields: StandardCustomFieldDraft[]
): PimCustomField[] {
  return fields.map((f) => ({
    key: f.key,
    label: f.label.trim(),
    allowsFile: !!f.allowsFile,
  }))
}

export function normalizeStandardCustomFields(
  raw: unknown
): StandardCustomFieldDraft[] {
  if (!Array.isArray(raw)) return []
  return raw.map((item, i) => {
    if (!item || typeof item !== 'object') {
      return createEmptyStandardCustomField(i)
    }
    const row = item as Record<string, unknown>
    const label = typeof row.label === 'string' ? row.label : ''
    const allowsFile = Boolean(row.allowsFile)
    const key =
      typeof row.key === 'string' && row.key.trim()
        ? row.key
        : slugifyFieldKey(label, i)
    return { key, label, allowsFile }
  })
}

/** Verbatim Origin validateAll / validateStep (index.html L7121–7122 / L5148–5149). */
export function validateStandardCustomFields(
  enabled: boolean,
  fields: StandardCustomFieldDraft[]
): string {
  if (!enabled) return ''
  if (fields.length === 0) {
    return 'A Standard-Custom product needs at least one custom field — add one, or turn off Standard-Custom.'
  }
  if (fields.some((f) => !f.label.trim())) {
    return 'Every custom field needs a label.'
  }
  return ''
}
